import { all, call, put, takeLatest, select } from "redux-saga/effects";
import { get } from "lodash";
import {
  getListDatasetDetailApi,
  deleteDatasetDetailsApi,
  getModuleCodeApi,
  getDataByAdvancedSearch,
  updateStatusImageApi,
  bulkUpdateStatusApi,
  getListPermissionByRole,
  getTargetLabels,
  autoLabelsApi,
} from "./api";
import {
  selectCurrentPage,
  selectLimit,
  selectQueriesField,
  selectCurrentDatasetId,
} from "./selectors";
import {
  GET_LIST_DATASET_DETAIL,
  showNoti,
  DATASET_DETAIL_CHANGE_LIMIT,
  DATASET_DETAIL_CHANGE_PAGE,
  FETCH_MODULE_CODE_REQUEST,
  DATASET_DETAIL_CHANGE_FILTER_CONDITION,
  DATASET_DETAIL_GET_DATA_BY_ADVANCED,
  DATASET_DETAIL_CHANGE_ADVANCED_FILTER_CONDITION,
  DATASET_DETAIL_CLEAR_ADVANCED_FILTER_CONDITION,
  DATASET_DETAIL_UPDATE_STATUS_IMAGE,
  DATASET_DETAIL_BULK_UPDATE_STATUS_REQUEST,
  DATASET_DETAIL_DELETE_IMAGES,
  CLEAR_ADVANCED_SEARCH_PAGE_CONDITION,
  DATASET_DETAIL_GET_ALL_DATASETS_OF_PROJECT,
  DATASET_DETAIL_GET_MATTRIX_PERMISSION,
  DATASET_DETAIL_GET_TARGET_LABELS,
  DATASET_DETAIL_AUTO_LABELS_REQUEST,
  DATASET_DETAIL_CHANGE_FILTER,
} from "redux/actions";
import { selectProjectId } from "redux/projects/selectors";
import {
  selectImageSizeFrom,
  selectImageSizeTo,
  selectDatasetIds,
  selectLabel,
  selectLabelExclude,
  selectLabelSize,
  selectResolution,
  selectSearchText,
  selectStatus,
  selectTagsInclude,
  selectTagsExclude,
  selectIsAdvancedSearch,
  selectAdvancedFilterByLabeler,
  selectAdvancedFilterByReviewer,
} from "./selectors";
import {
  ERROR_CASE,
  SUCCESS_CASE,
} from "components/notificationModal/constants";
import {
  getListDatasetDetailSuccess,
  fetchModuleCodeSuccess,
  getDataByAdvancedSearchSuccess,
  updateItemByStatusSuccess,
  bulkUpdateStatusSuccess,
  bulkUpdateStatusFailure,
  getListDatasetDetail,
  getDataByAdvancedSearch as fetchData,
  uncheckIds,
  toggleDeleteModal,
  getAllDatasetsInProjectsSuccess,
  getPermissionsByRoleSuccess,
  getTargetLabelsSuccess,
  autoLabelsSuccess,
  autoLabelFailure,
  onToggleAutoLabelDialog,
} from "./action";
import { fetchDatasets } from "hooks/api";

export function getSize(value, unit) {
  let returnValue = typeof value === "string" ? parseFloat(value) : value;
  switch (unit) {
    case "KB":
      returnValue = value * 1024;
      break;
    case "MB":
      returnValue = value * 1024 * 1024;
      break;
    case "GB":
      returnValue = value * 1024 * 1024 * 1024;
      break;
    default:
      break;
  }
  return returnValue;
}

function* getListDatasetDetailListSaga({ payload }) {
  const { dataset_id } = payload;
  const isAdvancedSearch = yield select(selectIsAdvancedSearch);
  if (isAdvancedSearch) return;

  const pageSize = yield select(selectLimit);
  const currentPage = yield select(selectCurrentPage);
  const queries = yield select(selectQueriesField);

  const searchAdvanced = Object.keys(queries)
    .filter((item) => queries[item] !== "" && queries[item]?.length !== 0)
    .map((key) => ({
      field: key,
      keywords:
        typeof queries[key] === "object" ? queries[key] : [queries[key]],
      condition:
        typeof queries[key] === "object" ? queries[key] : [queries[key]],
    }));

  try {
    const response = yield call(getListDatasetDetailApi, {
      dataset_id,
      pageSize,
      currentPage,
      searchAdvanced,
    });
    const { datasets_details, totalPage, totalItem, dataset_name } = get(
      response,
      "data.data",
      {
        totalItem: 0,
        totalPage: 0,
        datasets_details: [],
      },
    );

    yield put(
      getListDatasetDetailSuccess({
        datasetDetails: datasets_details,
        datasetName: dataset_name,
        pageSize,
        currentPage,
        dataset_id,
        totalPage,
        totalItem,
        queries,
      }),
    );
  } catch (error) {
    yield put(
      showNoti({
        type: ERROR_CASE,
        message: "Get List Dataset Detail Error!",
        messageId: "notification.get-list-dataset-detail-error",
      }),
    );
  }
}

function* getListDatasetDetailListByAdvancedSaga({ payload }) {
  const isAdvancedSearch = yield select(selectIsAdvancedSearch);
  if (!isAdvancedSearch) return;
  const projectId = yield select(selectProjectId);
  const pageSize = yield select(selectLimit);
  const currentPage = yield select(selectCurrentPage);
  const imageSizeFrom = yield select(selectImageSizeFrom);
  const imageSizeTo = yield select(selectImageSizeTo);
  const datasetIds = yield select(selectDatasetIds);
  const labelIds = yield select(selectLabel);
  const labelExcludeIds = yield select(selectLabelExclude);
  const labelingSize = yield select(selectLabelSize);
  const resolution = yield select(selectResolution);
  const searchText = yield select(selectSearchText);
  const status = yield select(selectStatus);
  const tagsInclude = yield select(selectTagsInclude);
  const tagsExclude = yield select(selectTagsExclude);
  const labeler = yield select(selectAdvancedFilterByLabeler);
  const reviewer = yield select(selectAdvancedFilterByReviewer);
  const objFilter = {
    ...(projectId && { projectId: [projectId] }),
    ...(payload?.projectId && { projectId: [payload.projectId] }),
    ...(imageSizeFrom.value !== "" &&
      imageSizeTo.value !== "" && {
      imageSize: {
        from: getSize(imageSizeFrom.value, imageSizeFrom.unit),
        to: getSize(imageSizeTo.value, imageSizeTo.unit),
      },
    }),
    ...(datasetIds.length > 0 && { datasetId: datasetIds }),
    ...(labelingSize.from !== "" &&
      labelingSize.to !== "" && {
      labelingSize: { from: labelingSize.from, to: labelingSize.to },
    }),
    ...((resolution.width !== "" || resolution.height !== "") && {
      resolution: {
        condition: resolution.condition,
        width: resolution.width ?? 0,
        height: resolution.height ?? 0,
      },
    }),
    ...(searchText && { text: searchText }),
    ...(status.length > 0 && { status }),
    ...(labeler && { labeler }),
    ...(reviewer && { reviewer }),
  };
  const search = Object.keys(objFilter).map((key) => ({
    field: key,
    value: objFilter[key],
  }));
  if (labelIds && labelIds.length > 0) {
    search.push({
      field: 'labelId',
      value: { queryType: 'is', labelIds: labelIds }
    });
  }
  if (labelExcludeIds && labelExcludeIds.length > 0) {
    search.push({
      field: 'labelId',
      value: { queryType: 'isNot', labelIds: labelExcludeIds }
    });
  }

  if (tagsInclude && tagsInclude.length > 0) {
    search.push({
      field: 'tag',
      value: {
        queryType: 'is',
        tagIds: tagsInclude.map((item) => item.tag_id)
      }
    });
  }
  if (tagsExclude && tagsExclude.length > 0) {
    search.push({
      field: 'tag',
      value: {
        queryType: 'isNot',
        tagIds: tagsExclude.map((item) => item.tag_id)
      }
    });
  }

  try {
    const response = yield call(getDataByAdvancedSearch, {
      pageSize,
      currentPage,
      search,
    });
    const { datasets_details, totalPage, totalItem } = get(
      response,
      "data.data",
      {
        totalItem: 0,
        totalPage: 0,
        datasets_details: [],
      },
    );
    yield put(
      getDataByAdvancedSearchSuccess({
        datasetDetails: datasets_details,
        totalPage,
        totalItem,
        objFilter,
      }),
    );
  } catch (error) {
    yield put(
      showNoti({
        type: ERROR_CASE,
        message: "Get List Dataset Detail Error!",
        messageId: "notification.get-list-dataset-detail-error",
      }),
    );
  }
}

function* fetchModuleCodeSaga({ payload }) {
  const { projectId, userId } = payload;

  try {
    const response = yield call(getModuleCodeApi, {
      projectId,
      userId,
    });
    yield put(
      fetchModuleCodeSuccess({
        data: response.data.data,
        roleCode: response.data.roleCode,
      }),
    );
  } catch (error) {
    yield put(
      showNoti({
        type: ERROR_CASE,
        message: "Get Module Code Error!",
        messageId: "notification.get-list-dataset-detail-error",
      }),
    );
  }
}

function* updateStatusImageSaga({ payload }) {
  const { sourceId, status } = payload;

  try {
    yield call(updateStatusImageApi, {
      sourceId,
      status,
    });
    const item = yield select(
      (state) => state.datasetDetail_v2.entities.items.byId[sourceId],
    );
    yield put(updateItemByStatusSuccess({ ...item, status }));
  } catch (error) {
    console.log(error);
  }
}

function* bulkUpdateStatusSaga({ payload }) {
  const isAdvancedSearch = yield select(selectIsAdvancedSearch);
  const dataset_id = yield select(selectCurrentDatasetId);

  try {
    yield call(bulkUpdateStatusApi, payload);
    yield put(bulkUpdateStatusSuccess());
    yield put(uncheckIds());

    if (isAdvancedSearch) {
      yield put(fetchData());
    } else {
      yield put(getListDatasetDetail({ dataset_id }));
    }

    yield put(
      showNoti({
        type: SUCCESS_CASE,
        message: "Status updated successfully!",
      }),
    );
  } catch (error) {
    yield put(bulkUpdateStatusFailure());
    yield put(
      showNoti({
        type: ERROR_CASE,
        message: "Update status error!",
      }),
    );
  }
}

function* deleteDatasetDetailsSaga({ payload }) {
  const { labelSources } = payload;
  const isAdvancedSearch = yield select(selectIsAdvancedSearch);
  const dataset_id = yield select(selectCurrentDatasetId);
  try {
    yield call(deleteDatasetDetailsApi, {
      labelSources,
      projectType: "image-object",
    });
    yield put(uncheckIds());
    yield put(toggleDeleteModal());
    if (!isAdvancedSearch) {
      yield put(getListDatasetDetail({ dataset_id }));
    } else {
      yield put(fetchData());
    }

    yield put(
      showNoti({
        type: SUCCESS_CASE,
        message: "Delete dataset detail successfully!",
      }),
    );
  } catch (error) {
    yield put(
      showNoti({
        type: ERROR_CASE,
        message: "Delete dataset detail Error!",
      }),
    );
  }
}

function* getAllDatasetsSaga({ payload }) {
  const { projectId } = payload;
  try {
    const res = yield call(fetchDatasets, projectId, 1, 1000);
    const data = res.datasets.map((i) => ({
      label: i.name,
      value: i.dataset_id,
    }));
    yield put(getAllDatasetsInProjectsSuccess(data));
  } catch (error) {
    yield put(
      showNoti({
        type: ERROR_CASE,
        message: "fetch datasets Error!",
      }),
    );
  }
}

function* getPermissionSaga({ payload }) {
  const { projectId, datasetId, sourceId } = payload;
  try {
    const res = yield call(getListPermissionByRole, {
      projectId,
      datasetId,
      sourceId,
    });
    yield put(getPermissionsByRoleSuccess(res.data?.data?.permissions ?? {}));
  } catch (error) {
    yield put(
      showNoti({
        type: ERROR_CASE,
        message: "fetch permission Error!",
      }),
    );
  }
}

function* getTargetLabelsSaga({ payload }) {
  const { projectId } = payload;
  try {
    const res = yield call(getTargetLabels, {
      projectId,
    });
    yield put(getTargetLabelsSuccess(res.data?.data));
  } catch (error) {
    yield put(
      showNoti({
        type: ERROR_CASE,
        message: "fetch target labels error!",
      }),
    );
  }
}

function* autoLabelsRequestSaga({ payload }) {
  try {
    if (payload.sourceIds.length > 1000) {
      yield put(
        showNoti({
          type: ERROR_CASE,
          message: "Choose too many items please select maximum 1000 items!",
        }),
      );
      yield put(uncheckIds());
      yield put(onToggleAutoLabelDialog());
      return;
    }
    yield call(autoLabelsApi, payload);
    yield put(autoLabelsSuccess());
    yield put(uncheckIds());
    yield put(onToggleAutoLabelDialog());
    yield put(
      showNoti({
        type: SUCCESS_CASE,
        message:
          "Auto-label request submitted successfully! Processing in background.",
      }),
    );
  } catch (error) {
    yield put(autoLabelFailure());
    yield put(
      showNoti({
        type: ERROR_CASE,
        message:
          error?.response?.data?.message || "auto labels error!",
      }),
    );
  }
}

export default function* rootSaga() {
  yield all([
    takeLatest(
      [
        GET_LIST_DATASET_DETAIL,
        DATASET_DETAIL_CHANGE_LIMIT,
        DATASET_DETAIL_CHANGE_PAGE,
        DATASET_DETAIL_CHANGE_FILTER_CONDITION,
        DATASET_DETAIL_CLEAR_ADVANCED_FILTER_CONDITION,
        DATASET_DETAIL_CHANGE_FILTER,
      ],
      getListDatasetDetailListSaga,
    ),
    takeLatest([DATASET_DETAIL_AUTO_LABELS_REQUEST], autoLabelsRequestSaga),
    takeLatest([DATASET_DETAIL_GET_TARGET_LABELS], getTargetLabelsSaga),
    takeLatest([FETCH_MODULE_CODE_REQUEST], fetchModuleCodeSaga),
    takeLatest(
      [
        DATASET_DETAIL_GET_DATA_BY_ADVANCED,
        DATASET_DETAIL_CHANGE_PAGE,
        DATASET_DETAIL_CHANGE_LIMIT,
        DATASET_DETAIL_CHANGE_ADVANCED_FILTER_CONDITION,
        CLEAR_ADVANCED_SEARCH_PAGE_CONDITION,
        DATASET_DETAIL_CHANGE_FILTER,
      ],
      getListDatasetDetailListByAdvancedSaga,
    ),
    takeLatest(DATASET_DETAIL_UPDATE_STATUS_IMAGE, updateStatusImageSaga),
    takeLatest(DATASET_DETAIL_BULK_UPDATE_STATUS_REQUEST, bulkUpdateStatusSaga),
    takeLatest(DATASET_DETAIL_DELETE_IMAGES, deleteDatasetDetailsSaga),
    takeLatest(DATASET_DETAIL_GET_ALL_DATASETS_OF_PROJECT, getAllDatasetsSaga),
    takeLatest(DATASET_DETAIL_GET_MATTRIX_PERMISSION, getPermissionSaga),
  ]);
}
